import React, { useState } from 'react';
import { Plus, Trash2, GripVertical, ChevronUp, ChevronDown } from 'lucide-react';
import Button from '../../Button';
import HelpTrigger from '../../../help/components/HelpTrigger';
import type { Question } from '../../../types/outreach';
import { QUESTION_TYPES } from '../../../constants/outreach';
import { QUESTION_TEMPLATES } from '../questionTemplates';

interface Props {
  questions: Question[];
  onQuestionsChange: (questions: Question[]) => void;
}

const WizardStep2Questions: React.FC<Props> = ({ questions, onQuestionsChange }) => {
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set());
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const addCustomQuestion = () => {
    const newQuestion: Question = {
      id: `q_${Date.now()}`,
      type: 'short_text',
      question: '',
      required: false,
      placeholder: ''
    };
    onQuestionsChange([...questions, newQuestion]);
    setEditingIndex(questions.length);
    setExpandedQuestions(new Set([...Array.from(expandedQuestions), questions.length]));
  };

  const addFromTemplate = (templateId: string) => {
    const template = QUESTION_TEMPLATES.find(t => t.id === templateId);
    if (!template) return;

    const newQuestion: Question = {
      id: `q_${Date.now()}`,
      type: template.type,
      question: template.question,
      required: template.required,
      placeholder: template.placeholder,
      options: template.options
    };

    onQuestionsChange([...questions, newQuestion]);
    setShowTemplatePicker(false);
    setExpandedQuestions(new Set([...Array.from(expandedQuestions), questions.length]));
  };

  const removeQuestion = (index: number) => {
    onQuestionsChange(questions.filter((_, i) => i !== index));
    if (editingIndex === index) {
      setEditingIndex(null);
    }
  };

  const updateQuestion = (index: number, updates: Partial<Question>) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], ...updates };
    onQuestionsChange(updated);
  };

  const toggleExpanded = (index: number) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedQuestions(newExpanded);
  };

  const moveQuestion = (fromIndex: number, toIndex: number) => {
    const newQuestions = [...questions];
    const [movedQuestion] = newQuestions.splice(fromIndex, 1);
    newQuestions.splice(toIndex, 0, movedQuestion);
    onQuestionsChange(newQuestions);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    moveQuestion(draggedIndex, index);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const addOption = (questionIndex: number) => {
    const question = questions[questionIndex];
    const newOption = { value: `option_${Date.now()}`, label: '' };
    updateQuestion(questionIndex, {
      options: [...(question.options || []), newOption]
    });
  };

  const updateOption = (questionIndex: number, optionIndex: number, label: string) => {
    const question = questions[questionIndex];
    const updatedOptions = [...(question.options || [])];
    updatedOptions[optionIndex] = { ...updatedOptions[optionIndex], label };
    updateQuestion(questionIndex, { options: updatedOptions });
  };

  const removeOption = (questionIndex: number, optionIndex: number) => {
    const question = questions[questionIndex];
    const updatedOptions = (question.options || []).filter((_, i) => i !== optionIndex);
    updateQuestion(questionIndex, { options: updatedOptions });
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary mb-2 flex items-center justify-center gap-2">
          Add Questions
          <HelpTrigger contentKey="outreach-questions" size="sm" />
        </h2>
        <p className="text-text-secondary dark:text-text-dark-secondary">
          Use smart templates or create custom questions
        </p>
      </div>

      {/* Question List */}
      <div className="space-y-3">
        {questions.map((question, index) => {
          const isExpanded = expandedQuestions.has(index);
          const isDragging = draggedIndex === index;
          return (
            <div 
              key={question.id} 
              className={`card transition-all ${isDragging ? 'opacity-50 scale-95' : 'opacity-100 scale-100'}`}
              onDragOver={(e) => handleDragOver(e, index)}
            >
              {/* Question Header */}
              <div className="p-4 flex items-center gap-3">
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  className="flex-shrink-0 cursor-move"
                >
                  <GripVertical className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-text-primary dark:text-text-dark-primary">
                    Question {index + 1}
                  </div>
                  <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
                    {QUESTION_TYPES.find(t => t.value === question.type)?.label || question.type}
                    {!isExpanded && question.question && ` • ${question.question.substring(0, 50)}${question.question.length > 50 ? '...' : ''}`}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleExpanded(index)}
                    className="p-2 text-text-secondary hover:text-text-primary dark:text-text-dark-secondary dark:hover:text-text-dark-primary transition-colors"
                  >
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  <Button variant="danger" size="sm" onClick={() => removeQuestion(index)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Question Edit Form */}
              {isExpanded && (
                <div className="p-4 pt-0 space-y-4">
                  {/* Question Text */}
                  <div>
                    <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                      Question Text *
                    </label>
                    <input
                      type="text"
                      value={question.question}
                      onChange={(e) => updateQuestion(index, { question: e.target.value })}
                      placeholder="Enter your question..."
                      className="input w-full"
                    />
                  </div>

                  {/* Type and Required */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                        Type
                      </label>
                      <select
                        value={question.type}
                        onChange={(e) => updateQuestion(index, { 
                          type: e.target.value as Question['type'],
                          options: (e.target.value === 'single_select' || e.target.value === 'multi_select') 
                            ? (question.options || [{ value: 'opt1', label: '' }])
                            : undefined
                        })}
                        className="input w-full"
                      >
                        {QUESTION_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={question.required}
                          onChange={(e) => updateQuestion(index, { required: e.target.checked })}
                          className="rounded"
                        />
                        <span className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                          Required
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Placeholder Text */}
                  {(question.type === 'short_text' || question.type === 'long_text' || question.type === 'email') && (
                    <div>
                      <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                        Placeholder Text
                      </label>
                      <input
                        type="text"
                        value={question.placeholder || ''}
                        onChange={(e) => updateQuestion(index, { placeholder: e.target.value })}
                        placeholder="Optional placeholder text..."
                        className="input w-full"
                      />
                    </div>
                  )}

                  {/* Options for Select Types */}
                  {(question.type === 'single_select' || question.type === 'multi_select') && (
                    <div>
                      <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                        Options
                      </label>
                      <div className="space-y-2">
                        {question.options?.map((option, optIndex) => (
                          <div key={option.value} className="flex gap-2">
                            <input
                              type="text"
                              value={option.label}
                              onChange={(e) => updateOption(index, optIndex, e.target.value)}
                              placeholder={`Option ${optIndex + 1}`}
                              className="input flex-1"
                            />
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => removeOption(index, optIndex)}
                              disabled={(question.options?.length || 0) <= 1}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => addOption(index)}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Option
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {questions.length === 0 && (
          <div className="text-center py-12 bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg">
            <p className="text-text-tertiary dark:text-text-dark-tertiary mb-4">
              No questions added yet
            </p>
            <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
              Add a custom question or choose from a template below
            </p>
          </div>
        )}
      </div>

      {/* Add Question Actions */}
      {showTemplatePicker ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-text-primary dark:text-text-dark-primary">
              Choose a Template
            </h3>
            <button
              onClick={() => setShowTemplatePicker(false)}
              className="text-sm text-text-secondary hover:text-text-primary dark:text-text-dark-secondary dark:hover:text-text-dark-primary"
            >
              Cancel
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {QUESTION_TEMPLATES.map((template) => {
              const Icon = template.icon;
              return (
                <button
                  key={template.id}
                  onClick={() => addFromTemplate(template.id)}
                  className="p-4 rounded-lg border border-border-light dark:border-border-dark hover:border-primary-500 dark:hover:border-primary-500 bg-white dark:bg-background-dark-secondary text-left transition-all"
                >
                  <Icon className="w-6 h-6 text-primary-600 mb-2" />
                  <div className="font-medium text-sm text-text-primary dark:text-text-dark-primary mb-1">
                    {template.name}
                  </div>
                  <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                    {template.description}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <Button variant="primary" onClick={addCustomQuestion} fullWidth>
            <Plus className="w-4 h-4 mr-2" />
            Add Custom Question
          </Button>
          <Button variant="secondary" onClick={() => setShowTemplatePicker(true)} fullWidth>
            <Plus className="w-4 h-4 mr-2" />
            Add from Template
          </Button>
        </div>
      )}
    </div>
  );
};

export default WizardStep2Questions;

